import type { CSSProperties } from 'react'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function localToday() {
  const d = new Date()
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-')
}

function addDays(iso: string, n: number) {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10)
}

function shortDate(iso: string) {
  const d = new Date(iso + 'T12:00:00')
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
}

// Fill in missing dates with null so charts have a continuous x-axis
function fillDates<T extends { log_date: string }>(
  rows: T[],
  days: number,
  today: string,
): (T | { log_date: string })[] {
  const map = new Map(rows.map((r) => [r.log_date, r]))
  return Array.from({ length: days }, (_, i) => {
    const d = addDays(today, -(days - 1) + i)
    return map.get(d) ?? { log_date: d }
  })
}

// ─── Weight line chart ────────────────────────────────────────────────────────

function WeightChart({ logs, days }: { logs: any[]; days: number }) {
  const today = localToday()
  const filled = fillDates(logs, days, today)
  const dataPoints = filled.filter((r: any) => r.weight_kg != null) as any[]

  if (dataPoints.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#52525b', fontSize: '0.8rem' }}>
        No weight logged yet for this period.
      </div>
    )
  }

  const W = 320
  const H = 120
  const PAD = { top: 8, right: 8, bottom: 24, left: 36 }
  const innerW = W - PAD.left - PAD.right
  const innerH = H - PAD.top - PAD.bottom

  const weights = dataPoints.map((r) => r.weight_kg)
  const minW = Math.min(...weights)
  const maxW = Math.max(...weights)
  const range = maxW - minW || 1

  const xOf = (iso: string) => {
    const idx = filled.findIndex((r) => r.log_date === iso)
    return PAD.left + (idx / (filled.length - 1)) * innerW
  }
  const yOf = (w: number) => PAD.top + innerH - ((w - minW) / range) * innerH

  const pathD = dataPoints
    .map((r, i) => `${i === 0 ? 'M' : 'L'} ${xOf(r.log_date)} ${yOf(r.weight_kg)}`)
    .join(' ')

  // Y-axis ticks
  const yTicks = [minW, (minW + maxW) / 2, maxW].map((v) => Math.round(v * 10) / 10)

  // X-axis labels — first, mid, last
  const xLabels = [filled[0], filled[Math.floor((filled.length - 1) / 2)], filled[filled.length - 1]]

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      style={{ width: '100%', height: 'auto', overflow: 'visible' }}
    >
      {/* Grid lines */}
      {yTicks.map((v) => (
        <line
          key={v}
          x1={PAD.left}
          y1={yOf(v)}
          x2={W - PAD.right}
          y2={yOf(v)}
          stroke="#27272a"
          strokeWidth={0.5}
        />
      ))}

      {/* Y-axis labels */}
      {yTicks.map((v) => (
        <text
          key={v}
          x={PAD.left - 4}
          y={yOf(v) + 3}
          textAnchor="end"
          fontSize={8}
          fill="#71717a"
        >
          {v}
        </text>
      ))}

      {/* X-axis labels */}
      {xLabels.map((r, i) => (
        <text
          key={r.log_date}
          x={xOf(r.log_date)}
          y={H - 4}
          textAnchor={i === 0 ? 'start' : i === xLabels.length - 1 ? 'end' : 'middle'}
          fontSize={8}
          fill="#71717a"
        >
          {shortDate(r.log_date)}
        </text>
      ))}

      {/* Line */}
      <path d={pathD} fill="none" stroke="#22d3ee" strokeWidth={1.5} strokeLinejoin="round" />

      {/* Dots */}
      {dataPoints.map((r) => (
        <circle
          key={r.log_date}
          cx={xOf(r.log_date)}
          cy={yOf(r.weight_kg)}
          r={2.5}
          fill="#22d3ee"
        />
      ))}

      {/* Latest value label */}
      {dataPoints.length > 0 && (() => {
        const last = dataPoints[dataPoints.length - 1]
        const cx = xOf(last.log_date)
        const cy = yOf(last.weight_kg)
        return (
          <text
            x={cx}
            y={cy - 6}
            textAnchor="middle"
            fontSize={9}
            fontWeight="bold"
            fill="#fafafa"
          >
            {last.weight_kg} kg
          </text>
        )
      })()}
    </svg>
  )
}

// ─── Calorie bar chart ────────────────────────────────────────────────────────

function CalorieChart({ rows, target }: { rows: any[]; target: number | null }) {
  const today = localToday()
  const days = 14
  const filled = fillDates(rows, days, today)

  const maxCal = Math.max(
    ...(filled as any[]).map((r: any) => r.calories ?? 0),
    target ?? 0,
    1,
  )

  const W = 320
  const H = 110
  const PAD = { top: 16, right: 8, bottom: 24, left: 36 }
  const innerW = W - PAD.left - PAD.right
  const innerH = H - PAD.top - PAD.bottom

  const barW = Math.max(innerW / days - 2, 4)
  const xOf = (i: number) => PAD.left + (i / days) * innerW + barW / 2
  const yOf = (cal: number) => PAD.top + innerH - (cal / maxCal) * innerH

  // Target line Y
  const targetY = target ? PAD.top + innerH - (target / maxCal) * innerH : null

  // X labels: just show Mon/first and last
  const showIdx = [0, Math.floor((days - 1) / 2), days - 1]

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      style={{ width: '100%', height: 'auto', overflow: 'visible' }}
    >
      {/* Y-axis ticks */}
      {[0, Math.round(maxCal / 2), Math.round(maxCal)].map((v) => (
        <g key={v}>
          <line
            x1={PAD.left}
            y1={yOf(v)}
            x2={W - PAD.right}
            y2={yOf(v)}
            stroke="#27272a"
            strokeWidth={0.5}
          />
          <text x={PAD.left - 4} y={yOf(v) + 3} textAnchor="end" fontSize={8} fill="#71717a">
            {v > 0 ? v : ''}
          </text>
        </g>
      ))}

      {/* Target line */}
      {targetY != null && (
        <>
          <line
            x1={PAD.left}
            y1={targetY}
            x2={W - PAD.right}
            y2={targetY}
            stroke="#f59e0b"
            strokeWidth={1}
            strokeDasharray="3 3"
          />
          <text x={W - PAD.right + 2} y={targetY + 3} fontSize={7} fill="#f59e0b">
            target
          </text>
        </>
      )}

      {/* Bars */}
      {(filled as any[]).map((r, i) => {
        const cal = r.calories ?? 0
        const over = target && cal > target
        const barH = (cal / maxCal) * innerH
        return (
          <rect
            key={r.log_date}
            x={xOf(i) - barW / 2}
            y={yOf(cal)}
            width={barW}
            height={barH}
            rx={1}
            fill={cal === 0 ? '#1c1c1f' : over ? '#f87171' : '#22d3ee'}
            opacity={cal === 0 ? 0.4 : 0.85}
          />
        )
      })}

      {/* X labels */}
      {showIdx.map((i) => (
        <text
          key={i}
          x={xOf(i)}
          y={H - 4}
          textAnchor={i === 0 ? 'start' : i === days - 1 ? 'end' : 'middle'}
          fontSize={8}
          fill="#71717a"
        >
          {shortDate(filled[i].log_date)}
        </text>
      ))}
    </svg>
  )
}

// ─── Macro average tiles ──────────────────────────────────────────────────────

function MacroAverages({
  rows,
  targets,
}: {
  rows: any[]
  targets: any | null
}) {
  const logged = rows.filter((r) => r.calories > 0)
  if (logged.length === 0) return null

  const avg = (key: string) =>
    Math.round(logged.reduce((s, r) => s + (r[key] ?? 0), 0) / logged.length)

  const items = [
    { label: 'Calories', value: avg('calories'), unit: 'kcal', target: targets?.target_calories, color: '#fafafa' },
    { label: 'Protein',  value: avg('protein_g'), unit: 'g',   target: targets?.target_protein_g, color: '#22d3ee' },
    { label: 'Carbs',    value: avg('carbs_g'),   unit: 'g',   target: targets?.target_carbs_g,   color: '#a78bfa' },
    { label: 'Fat',      value: avg('fat_g'),      unit: 'g',   target: targets?.target_fat_g,     color: '#fb923c' },
  ]

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem' }}>
      {items.map(({ label, value, unit, target, color }) => (
        <div
          key={label}
          style={{
            background: '#18181b',
            borderRadius: 8,
            padding: '0.5rem',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '1rem', fontWeight: 700, color }}>
            {value}
            <span style={{ fontSize: '0.55rem', color: '#71717a', fontWeight: 400 }}>{unit}</span>
          </div>
          {target && (
            <div style={{ fontSize: '0.6rem', color: '#52525b' }}>/ {Math.round(target)}</div>
          )}
          <div style={{ fontSize: '0.6rem', color: '#71717a', marginTop: 2 }}>{label}</div>
        </div>
      ))}
    </div>
  )
}

// ─── Change stat ──────────────────────────────────────────────────────────────

function WeightChange({ logs }: { logs: any[] }) {
  if (logs.length < 2) return null
  const sorted = [...logs].sort((a, b) => a.log_date.localeCompare(b.log_date))
  const first = sorted[0].weight_kg
  const last = sorted[sorted.length - 1].weight_kg
  const diff = Math.round((last - first) * 10) / 10
  const sign = diff > 0 ? '+' : ''
  const color = diff < 0 ? '#4ade80' : diff > 0 ? '#f87171' : '#71717a'

  return (
    <span style={{ fontSize: '0.8rem', color, fontWeight: 600 }}>
      {sign}{diff} kg
    </span>
  )
}

// ─── ProgressPage ─────────────────────────────────────────────────────────────

const WEIGHT_PERIODS = [
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
]

export default function ProgressPage() {
  const [weightDays, setWeightDays] = useState(30)
  const today = localToday()

  const { data: weightLogs = [], isLoading: wLoading } = useQuery({
    queryKey: ['logs', 'weight'],
    queryFn: () => api.logs.weight.list(90),
  })

  const { data: calRows = [], isLoading: cLoading } = useQuery({
    queryKey: ['summary', 'week', 14],
    queryFn: () => api.summary.week(today, 14),
  })

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.profile.get(),
  })

  const isLoading = wLoading || cLoading

  // Weight logs for selected period
  const cutoff = addDays(today, -(weightDays - 1))
  const periodLogs = (weightLogs as any[]).filter((r: any) => r.log_date >= cutoff)

  // 7-day calorie rows for macro averages
  const sevenDayCutoff = addDays(today, -6)
  const recentRows = (calRows as any[]).filter((r: any) => r.log_date >= sevenDayCutoff)

  return (
    <div style={{ paddingBottom: '2rem' }}>
      <div
        style={{
          padding: '0.75rem 1rem',
          borderBottom: '1px solid #27272a',
          fontWeight: 600,
          fontSize: '0.95rem',
        }}
      >
        Progress
      </div>

      {isLoading ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#71717a' }}>Loading…</div>
      ) : (
        <>
          {/* ── Weight ── */}
          <section style={card}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={cardHeading}>Weight</span>
                <WeightChange logs={periodLogs} />
              </div>
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                {WEIGHT_PERIODS.map((p) => (
                  <button
                    key={p.days}
                    onClick={() => setWeightDays(p.days)}
                    style={{
                      background: weightDays === p.days ? '#27272a' : 'none',
                      border: `1px solid ${weightDays === p.days ? '#3f3f46' : '#27272a'}`,
                      borderRadius: 5,
                      color: weightDays === p.days ? '#fafafa' : '#71717a',
                      fontSize: '0.7rem',
                      padding: '0.2rem 0.45rem',
                      cursor: 'pointer',
                    }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <WeightChart logs={periodLogs} days={weightDays} />
          </section>

          {/* ── Calorie history ── */}
          <section style={card}>
            <div style={{ marginBottom: '0.75rem' }}>
              <span style={cardHeading}>Calories — last 14 days</span>
              {profile?.target_calories && (
                <span style={{ fontSize: '0.7rem', color: '#71717a', marginLeft: '0.5rem' }}>
                  target {Math.round(profile.target_calories)} kcal shown in amber
                </span>
              )}
            </div>
            <CalorieChart rows={calRows as any[]} target={profile?.target_calories ?? null} />
          </section>

          {/* ── 7-day macro averages ── */}
          <section style={card}>
            <div style={{ marginBottom: '0.75rem' }}>
              <span style={cardHeading}>7-day averages</span>
              <span style={{ fontSize: '0.7rem', color: '#71717a', marginLeft: '0.5rem' }}>
                logged days only
              </span>
            </div>
            <MacroAverages rows={recentRows} targets={profile} />
            {recentRows.filter((r: any) => r.calories > 0).length === 0 && (
              <div style={{ fontSize: '0.8rem', color: '#52525b' }}>
                No food logged in the last 7 days.
              </div>
            )}
          </section>
        </>
      )}
    </div>
  )
}

// ─── Shared styles ────────────────────────────────────────────────────────────

const card: CSSProperties = {
  padding: '0.9rem 1rem',
  borderBottom: '1px solid #18181b',
}

const cardHeading: CSSProperties = {
  fontSize: '0.78rem',
  fontWeight: 600,
  color: '#a1a1aa',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
}
